import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSidebar, API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { usePushNotification } from '@/context/PushNotificationContext';
import { Menu, Bell, Search, User, Volume2, VolumeX, CheckCheck, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import NotificationSettings from '@/components/NotificationSettings';

export default function Header() {
  const { setSidebarOpen, isMobile } = useSidebar();
  const { isDark, toggleTheme } = useTheme();
  const { notifyNewEnquiry, permission } = usePushNotification();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [user, setUser] = useState(null);
  const previousEnquiryIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  // Get user info
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Play second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.3);
      }, 200);
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
  };

  // Fetch enquiries and check for new ones
  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/enquiries`);
      const enquiries = response.data;
      
      // Get read notification IDs from localStorage
      const readIds = new Set(JSON.parse(localStorage.getItem('readNotificationIds') || '[]'));
      
      // Current enquiry IDs
      const currentIds = new Set(enquiries.map(e => e.id));
      
      // Find truly NEW enquiries (not in previous fetch AND not in first load)
      const newEnquiries = [];
      if (!isFirstLoadRef.current) {
        enquiries.forEach(e => {
          if (!previousEnquiryIdsRef.current.has(e.id)) {
            newEnquiries.push(e);
          }
        });
      }
      
      // Update previous IDs reference
      previousEnquiryIdsRef.current = currentIds;
      isFirstLoadRef.current = false;
      
      // Update notifications with latest enquiries
      const latestNotifications = enquiries
        .slice(0, 10)
        .map(e => ({
          id: e.id,
          title: e.subject || 'New Enquiry',
          message: `${e.name}: ${e.message?.substring(0, 50)}...`,
          time: e.created_at,
          isNew: !readIds.has(e.id),
          type: 'enquiry',
          source: e.source
        }));
      
      setNotifications(latestNotifications);
      
      // Calculate unread count (notifications not marked as read)
      const unreadNotifications = latestNotifications.filter(n => n.isNew);
      setUnreadCount(unreadNotifications.length);
      
      // If there are truly NEW enquiries (just arrived), play sound, animate, and send push notification
      if (newEnquiries.length > 0) {
        playNotificationSound();
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
        
        // Send push notification for each new enquiry
        newEnquiries.forEach(enquiry => {
          notifyNewEnquiry(enquiry);
        });
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
  };

  // Poll for new enquiries every 10 seconds
  useEffect(() => {
    fetchEnquiries();
    const interval = setInterval(fetchEnquiries, 10000);
    return () => clearInterval(interval);
  }, [soundEnabled]);

  // Mark all as read
  const markAllAsRead = () => {
    const allIds = notifications.map(n => n.id);
    localStorage.setItem('readNotificationIds', JSON.stringify(allIds));
    setNotifications(prev => prev.map(n => ({ ...n, isNew: false })));
    setUnreadCount(0);
  };

  // Mark single notification as read
  const markAsRead = (notificationId) => {
    const readIds = JSON.parse(localStorage.getItem('readNotificationIds') || '[]');
    if (!readIds.includes(notificationId)) {
      readIds.push(notificationId);
      localStorage.setItem('readNotificationIds', JSON.stringify(readIds));
    }
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, isNew: false } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Navigate to enquiries page
  const goToEnquiries = (notificationId) => {
    if (notificationId) {
      markAsRead(notificationId);
    }
    navigate('/admin/enquiries');
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user?.name) return 'AD';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  return (
    <header className={`sticky top-0 z-30 h-16 border-b shadow-sm ${adminTheme.headerBg(isDark)}`}>
      <div className="h-full px-4 lg:px-6 flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className={isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
          )}
          
          {/* Search */}
          <div className="hidden md:flex items-center">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                placeholder="Search..."
                className={`w-64 pl-10 ${adminTheme.inputBg(isDark)}`}
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className={isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle-btn"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {/* Sound Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}
            title={soundEnabled ? 'Mute notifications' : 'Enable notification sound'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>

          {/* Push Notification Settings */}
          <NotificationSettings />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={`relative ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'} ${isAnimating ? 'animate-bounce' : ''}`} 
                data-testid="notifications-btn"
              >
                <Bell className={`w-5 h-5 ${isAnimating ? 'text-blue-400' : ''}`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <>
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                        {unreadCount} new
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markAllAsRead();
                        }}
                      >
                        <CheckCheck className="w-3 h-3 mr-1" />
                        Read All
                      </Button>
                    </>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-72 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`flex gap-3 py-3 px-3 cursor-pointer hover:bg-slate-50 transition-colors ${notification.isNew ? 'bg-blue-50' : ''}`}
                      onClick={() => goToEnquiries(notification.id)}
                    >
                      <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${notification.isNew ? 'bg-blue-500' : 'bg-slate-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${notification.isNew ? 'text-blue-900' : 'text-slate-900'}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{notification.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(notification.time)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center text-slate-500 text-sm">
                    No notifications yet
                  </div>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="justify-center text-blue-600 cursor-pointer"
                onClick={() => goToEnquiries()}
              >
                View all enquiries
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={`flex items-center gap-2 px-2 ${isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`} data-testid="user-menu-btn">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-blue-600 text-white text-sm">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.name || 'Admin'}</p>
                  <p className={`text-xs capitalize ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user?.role?.replace('_', ' ') || 'Administrator'}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-blue-600 text-white">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{user?.name || 'Admin User'}</p>
                    <p className="text-xs text-slate-500">{user?.email || 'admin@aurous.com'}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600 cursor-pointer" onClick={handleLogout}>
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
